// ════════════════════════════════════════════════════════════════
// FloorCost — Passkey (WebAuthn + PRF)
// ────────────────────────────────────────────────────────────────
// Passkey-primary, magic-link-fallback sign-in. State of the art
// for a client-side-only architecture:
//
//   1. After magic-link sign-in, the operator registers a passkey.
//   2. Registration creates a platform credential (Touch ID / Face
//      ID / device passcode) AND requests the PRF extension —
//      WebAuthn's "hmac-secret" derived bytes that only the
//      authenticator can produce.
//   3. Those PRF bytes feed HKDF → an AES-GCM key → encrypts the
//      Supabase refresh_token. Ciphertext lives in localStorage.
//   4. On next visit, "Sign in with passkey" runs the WebAuthn
//      ceremony, re-derives the same AES-GCM key from the PRF
//      output, decrypts the refresh_token, and exchanges it for a
//      fresh Supabase session.
//
// Why this matters: even if localStorage is exfiltrated, the
// ciphertext is useless without the authenticator. The PRF output
// never leaves the device. Conventional "store refresh_token in
// localStorage" leaks the session on first XSS / device theft;
// this design upgrades the wrapped credential to authenticator-
// gated cryptography.
//
// Refresh-token rotation: Supabase mints a new refresh_token on
// every refresh grant. After signInWithPasskey() succeeds we
// re-encrypt the rotated token under the same PRF salt + a fresh
// IV so the wrapped credential stays current.
//
// Browser compat: PRF extension requires Safari 18+ (macOS &
// iPadOS) and Chrome 116+. Pilot devices (operator's iPad +
// MacBook) are both inside this window. Older browsers detect via
// `isPasskeySupported()` and the registration flow surfaces a
// useful error.
// ════════════════════════════════════════════════════════════════

import { STORAGE } from '#lib/config.js';
import { lsSet } from '#features/sync/storage.js';
import { refreshSession, getSession } from './index.js';

const KEY = STORAGE.passkey;
const RP_NAME = 'FloorCost';
const HKDF_INFO_BYTES = new TextEncoder().encode('fc-passkey-refresh-token-aes-gcm-v1');

/**
 * @typedef {Object} PasskeyRecord
 * @property {string} credentialId  base64url-encoded credential id (rawId)
 * @property {string} prfSalt       base64url-encoded 32-byte PRF salt
 * @property {string} iv            base64url-encoded 12-byte AES-GCM IV
 * @property {string} ciphertext    base64url-encoded AES-GCM ciphertext+tag of the refresh_token
 * @property {string} userEmail     for display in the settings sheet
 * @property {string} userId        Supabase auth.uid()
 * @property {number} createdAt     unix ms when this passkey was registered
 */

// ── Public surface ───────────────────────────────────────────────

/**
 * Feature-detect WebAuthn + platform authenticator + PRF support.
 * Returns false on older browsers and on environments without a
 * platform authenticator (most desktop Linux, some private modes).
 *
 * Does NOT call out to navigator.credentials — only static checks.
 * The PRF support check happens during registration when we ask
 * the authenticator to evaluate it.
 *
 * @returns {boolean}
 */
export function isPasskeySupported() {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials !== 'undefined' &&
    typeof navigator.credentials.create === 'function' &&
    typeof navigator.credentials.get === 'function' &&
    typeof crypto !== 'undefined' &&
    typeof crypto.subtle !== 'undefined'
  );
}

/**
 * @returns {boolean}  true iff a passkey record is in localStorage.
 */
export function hasPasskey() {
  return getPasskeyMeta() !== null;
}

/**
 * @returns {{ userEmail: string, createdAt: number } | null}
 *   Surface bits used by the settings sheet. Returns null if no
 *   passkey is registered.
 */
export function getPasskeyMeta() {
  const rec = _readRecord();
  if (!rec) return null;
  return { userEmail: rec.userEmail, createdAt: rec.createdAt };
}

/**
 * Forget the locally-wrapped credential. The platform passkey
 * itself isn't deleted (the operating system owns its lifecycle —
 * they can remove it from Settings ▸ Passwords if they want), but
 * FloorCost will no longer offer "Sign in with passkey" until they
 * register again.
 *
 * @returns {void}
 */
export function removePasskey() {
  try { localStorage.removeItem(KEY); } catch { /* private mode */ }
}

/**
 * Run the WebAuthn registration ceremony, derive an AES-GCM key
 * from the PRF output, and wrap the current session's refresh
 * token. Requires an active session — call after magic-link sign-
 * in succeeds.
 *
 * @returns {Promise<void>}
 * @throws if no active session, if PRF unsupported, or the user cancels.
 */
export async function registerPasskey() {
  if (!isPasskeySupported()) {
    throw new Error('Passkeys are not supported on this device.');
  }
  const session = getSession();
  if (!session) {
    throw new Error('Sign in first — passkeys wrap an existing session.');
  }
  const { user, refresh_token } = session;
  if (!refresh_token) {
    throw new Error('Active session has no refresh token to wrap.');
  }

  const prfSalt = crypto.getRandomValues(new Uint8Array(32));
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const userIdBytes = _userIdToBytes(user.id);

  const created = /** @type {PublicKeyCredential | null} */ (
    await navigator.credentials.create({
      publicKey: {
        rp: { name: RP_NAME, id: window.location.hostname },
        user: {
          id: userIdBytes,
          name: user.email || 'operator',
          displayName: user.email || 'FloorCost operator',
        },
        challenge,
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },   // ES256 — broadest support
          { type: 'public-key', alg: -257 }, // RS256 — Windows Hello fallback
        ],
        authenticatorSelection: {
          // No `authenticatorAttachment` constraint — leaving this
          // unset lets the browser present both options at create()
          // time: the platform authenticator (Windows Hello, Touch
          // ID, Android biometric) AND the cross-device flow that
          // QR-codes to a phone with iCloud Keychain / Google
          // Password Manager. The operator chose the cross-device
          // path explicitly (iPhone Face ID syncing via iCloud
          // Keychain) so the laptop and the iPad share one passkey.
          // Constraining to 'platform' here would hide the
          // "Use a different device" option entirely.
          residentKey: 'required',
          userVerification: 'required',
        },
        timeout: 60_000,
        attestation: 'none',
        extensions: {
          // Try evaluating PRF inline. Modern Safari/Chrome return
          // the output directly; older builds return enabled=true
          // and require a follow-up get() to actually evaluate.
          prf: { eval: { first: prfSalt } },
        },
      },
    })
  );

  if (!created) throw new Error('Passkey registration was cancelled.');
  const credentialId = new Uint8Array(created.rawId);
  const extRes = created.getClientExtensionResults();
  const prfResult = extRes && extRes.prf;
  let prfOutput = prfResult && prfResult.results && prfResult.results.first;

  if (!prfOutput) {
    // Authenticator returned enabled=true (PRF supported) but
    // didn't evaluate inline. Run an immediate get() to obtain
    // the PRF bytes. This is the safe two-step pattern.
    if (!prfResult || prfResult.enabled !== true) {
      throw new Error('This authenticator does not support PRF — passkey sign-in is unavailable. Stick with magic-link.');
    }
    const challenge2 = crypto.getRandomValues(new Uint8Array(32));
    const asserted = /** @type {PublicKeyCredential | null} */ (
      await navigator.credentials.get({
        publicKey: {
          challenge: challenge2,
          rpId: window.location.hostname,
          allowCredentials: [{ id: credentialId, type: 'public-key' }],
          userVerification: 'required',
          timeout: 60_000,
          extensions: {
            prf: { eval: { first: prfSalt } },
          },
        },
      })
    );
    if (!asserted) throw new Error('Passkey registration verification cancelled.');
    const ext2 = asserted.getClientExtensionResults();
    prfOutput = ext2 && ext2.prf && ext2.prf.results && ext2.prf.results.first;
    if (!prfOutput) {
      throw new Error('Authenticator returned no PRF output — passkey sign-in is unavailable.');
    }
  }

  const aesKey = await _deriveKey(new Uint8Array(prfOutput), prfSalt);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      aesKey,
      new TextEncoder().encode(refresh_token),
    ),
  );

  /** @type {PasskeyRecord} */
  const record = {
    credentialId: _b64uEncode(credentialId),
    prfSalt: _b64uEncode(prfSalt),
    iv: _b64uEncode(iv),
    ciphertext: _b64uEncode(ciphertext),
    userEmail: user.email || '',
    userId: user.id || '',
    createdAt: Date.now(),
  };
  lsSet(KEY, JSON.stringify(record));
}

/**
 * Run the WebAuthn assertion ceremony, decrypt the wrapped refresh
 * token, exchange it for a fresh Supabase session, then re-encrypt
 * the rotated refresh token under the same PRF salt + a fresh IV.
 *
 * The session-change listener installed by boot.js will route the
 * operator into the app on success.
 *
 * @returns {Promise<void>}
 * @throws if no record present, the ceremony fails, decryption
 *         fails (ciphertext tampered), or the refresh exchange
 *         fails (revoked token).
 */
export async function signInWithPasskey() {
  if (!isPasskeySupported()) {
    throw new Error('Passkeys are not supported on this device.');
  }
  const rec = _readRecord();
  if (!rec) throw new Error('No passkey registered on this device.');

  const credentialId = _b64uDecode(rec.credentialId);
  const prfSalt = _b64uDecode(rec.prfSalt);
  const challenge = crypto.getRandomValues(new Uint8Array(32));

  const asserted = /** @type {PublicKeyCredential | null} */ (
    await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId: window.location.hostname,
        allowCredentials: [{ id: credentialId, type: 'public-key' }],
        userVerification: 'required',
        timeout: 60_000,
        extensions: {
          prf: { eval: { first: prfSalt } },
        },
      },
    })
  );
  if (!asserted) throw new Error('Passkey sign-in was cancelled.');
  const ext = asserted.getClientExtensionResults();
  const prfOutput = ext && ext.prf && ext.prf.results && ext.prf.results.first;
  if (!prfOutput) {
    throw new Error('Authenticator returned no PRF output — cannot decrypt session.');
  }

  const aesKey = await _deriveKey(new Uint8Array(prfOutput), prfSalt);
  const iv = _b64uDecode(rec.iv);
  const ciphertext = _b64uDecode(rec.ciphertext);

  /** @type {string} */
  let refreshToken;
  try {
    const plaintext = new Uint8Array(
      await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, ciphertext),
    );
    refreshToken = new TextDecoder().decode(plaintext);
  } catch {
    throw new Error('Could not decrypt passkey envelope — try magic-link sign-in and re-register.');
  }

  const session = await refreshSession(refreshToken);

  // Rotated refresh token — re-encrypt under same PRF salt so the
  // record stays current. New IV per encryption (AES-GCM IV reuse
  // breaks confidentiality).
  const newIv = crypto.getRandomValues(new Uint8Array(12));
  const newCt = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: newIv },
      aesKey,
      new TextEncoder().encode(session.refresh_token),
    ),
  );
  /** @type {PasskeyRecord} */
  const updated = {
    ...rec,
    iv: _b64uEncode(newIv),
    ciphertext: _b64uEncode(newCt),
  };
  lsSet(KEY, JSON.stringify(updated));
}

// ── Internals ────────────────────────────────────────────────────

/** @returns {PasskeyRecord | null} */
function _readRecord() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw);
    if (!rec || typeof rec !== 'object') return null;
    if (!rec.credentialId || !rec.prfSalt || !rec.iv || !rec.ciphertext) return null;
    return rec;
  } catch {
    return null;
  }
}

/**
 * HKDF-SHA256 over the PRF output, salted with the same bytes we
 * fed to the authenticator. Output is a non-extractable AES-GCM
 * key — once derived it can encrypt/decrypt but can't be exported
 * via crypto.subtle.exportKey, so a debugging foot-gun can't
 * accidentally leak it.
 *
 * @param {Uint8Array} prfBytes
 * @param {Uint8Array} salt
 * @returns {Promise<CryptoKey>}
 */
export async function _deriveKey(prfBytes, salt) {
  const hkdfKey = await crypto.subtle.importKey(
    'raw',
    prfBytes,
    'HKDF',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt,
      info: HKDF_INFO_BYTES,
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Encode a UUID-style user.id string into a byte array suitable
 * for the WebAuthn `user.id` field (must be 1–64 bytes). Falls
 * back to UTF-8 bytes of whatever was provided.
 *
 * Exported for tests.
 *
 * @param {string} id
 * @returns {Uint8Array}
 */
export function _userIdToBytes(id) {
  if (!id) return new TextEncoder().encode('anonymous');
  // Strip UUID dashes and decode as hex if it looks like a UUID.
  const hex = id.replace(/-/g, '');
  if (/^[0-9a-fA-F]{32}$/.test(hex)) {
    const out = new Uint8Array(16);
    for (let i = 0; i < 16; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    return out;
  }
  return new TextEncoder().encode(id);
}

/**
 * base64url encode a byte array (no padding). Exported for tests.
 *
 * @param {Uint8Array} bytes
 * @returns {string}
 */
export function _b64uEncode(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}

/**
 * base64url decode to a byte array. Exported for tests.
 *
 * @param {string} s
 * @returns {Uint8Array}
 */
export function _b64uDecode(s) {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/') +
    '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
